// CoverageTrendView — the "Trend" view for logging_rate: the running completion
// rate within the selected period. At each cadence bucket (day / week / month)
// the line shows the coverage SO FAR — (buckets logged from the period start
// through this point) / (buckets elapsed so far). So a month window's day 3 plots
// (days 1–3 with a check-in) / 3, and the line converges to the period's overall
// % by its end (the same number the calendar's banner shows).
//
// Stateless like the other bodies: it computes from the card's already-fetched
// `activeDates` (the 'active-dates' source), no fetch of its own. An 'All'
// window starts at the first day with data (`pagingDates`), which on a yes/no
// rate is the first answer rather than the first counted day — the backend's
// denominator starts there too, so the line ends on the banner's %. Buckets whose
// start is after today are dropped (the line stops at the present), matching the
// backend's today-clamp. Weeks are Sunday-anchored, matching StreakStripView and
// the backend `_bucket_index` (which keep parallel copies of this bucketing —
// change all three together).

import React, { useMemo } from 'react'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts'
import type { DateWindow } from '../../../charts/windowRange'
import type { SummaryViewBodyProps } from '../viewRegistry'
import { COVERAGE_COLOR } from './streakCommon'
import { isoDay } from '../../../charts/bodies/boolAggregation'
import {
  dayTicks, formatDateLabel, formatMonthLabel, yearBoundaries, yearBoundaryLineProps,
} from '../../../charts/timeAxis'

type Cadence = 'daily' | 'weekly' | 'monthly'

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const dayStartMs = (ms: number): number => {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}
const weekStartMs = (ms: number): number => {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay()).getTime()
}
const monthStartMs = (ms: number): number => {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime()
}

function bucketStartMs(ms: number, c: Cadence): number {
  return c === 'weekly' ? weekStartMs(ms) : c === 'monthly' ? monthStartMs(ms) : dayStartMs(ms)
}
function nextBucketMs(ms: number, c: Cadence): number {
  const d = new Date(ms)
  return c === 'weekly'
    ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7).getTime()
    : c === 'monthly'
      ? new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime()
      : new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime()
}
// A day-resolution key of the bucket START uniquely identifies a day, a
// Sunday-anchored week, or a calendar month.
function bucketKey(ms: number, c: Cadence): string {
  const d = new Date(bucketStartMs(ms, c))
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

export interface CoverageTrendPoint {
  /** Bucket start (ms) — the x position. */
  date: number
  /** Running coverage % through this bucket (0–100, one decimal). */
  value: number
  /** Cumulative logged buckets through this point. */
  active: number
  /** Cumulative buckets elapsed through this point (the denominator). */
  total: number
}

/** Running completion % per cadence bucket across the window. Pure, so the view
 *  and its tests share one definition; `todayMs` is injected to stop the line at
 *  the present (no future buckets) rather than reading the wall clock.
 *  `spanDates` are the days with data, counted or not; an unbounded window
 *  starts at the earliest of them (default: the counted days themselves). */
export function computeCoverageTrend(
  activeDates: number[],
  window: DateWindow,
  cadence: Cadence,
  todayMs: number,
  spanDates: number[] = activeDates,
): CoverageTrendPoint[] {
  const dayAfterToday = nextBucketMs(dayStartMs(todayMs), 'daily')
  const finite = Number.isFinite(window.start) && Number.isFinite(window.end)

  let spanStart: number
  let spanEnd: number
  if (finite) {
    spanStart = window.start
    spanEnd = Math.min(window.end, dayAfterToday)
  } else {
    const withData = [...spanDates, ...activeDates]
    if (withData.length === 0) return []
    spanStart = Math.min(...withData)
    spanEnd = dayAfterToday
  }
  if (spanEnd <= spanStart) return []

  const activeKeys = new Set<string>()
  for (const ms of activeDates) {
    if (ms >= spanStart && ms < spanEnd) activeKeys.add(bucketKey(ms, cadence))
  }

  const points: CoverageTrendPoint[] = []
  let total = 0
  let active = 0
  let guard = 0
  for (let ms = bucketStartMs(spanStart, cadence); ms < spanEnd; ms = nextBucketMs(ms, cadence)) {
    if (++guard > 4000) break // runaway guard (years of daily buckets)
    total += 1
    if (activeKeys.has(bucketKey(ms, cadence))) active += 1
    points.push({ date: ms, value: Math.round((active / total) * 1000) / 10, active, total })
  }
  return points
}

const CoverageTrendView: React.FC<SummaryViewBodyProps> = ({
  config,
  window,
  activeDates = [],
  pagingDates = activeDates,
}) => {
  const cadence: Cadence =
    (config?.params as { cadence?: string } | null | undefined)?.cadence === 'weekly'
      ? 'weekly'
      : (config?.params as { cadence?: string } | null | undefined)?.cadence === 'monthly'
        ? 'monthly'
        : 'daily'
  const unit = cadence === 'weekly' ? 'weeks' : cadence === 'monthly' ? 'months' : 'days'

  const data = useMemo(
    () => computeCoverageTrend(activeDates, window, cadence, Date.now(), pagingDates),
    [activeDates, window, cadence, pagingDates],
  )

  // The axis is the shared one (`charts/timeAxis`): day ticks read `Thu 10/1`
  // and the year is a line, not part of each label. A monthly line ticks on its
  // own month buckets instead, so no tick names a mid-month day.
  const fmtX = (ts: number): string =>
    cadence === 'monthly' ? formatMonthLabel(ts) : formatDateLabel(ts, false)
  const fmtFull = (ts: number): string => {
    const d = new Date(ts)
    return cadence === 'monthly'
      ? `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const xTicks = useMemo<number[] | undefined>(() => {
    if (data.length === 0) return undefined
    const dates = data.map((d) => d.date)
    if (cadence !== 'monthly') return dayTicks(dates[0], dates[dates.length - 1])
    const step = Math.ceil(dates.length / 6)
    return dates.filter((_, i) => i % step === 0)
  }, [data, cadence])

  const yearLines = useMemo<number[]>(
    () => (data.length < 2 ? [] : yearBoundaries(data[0].date, data[data.length - 1].date)),
    [data],
  )

  if (data.length === 0) {
    return (
      <div data-testid="summary-coverage-trend" data-point-count={0}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">
          <p>No check-ins yet.</p>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="summary-coverage-trend"
      data-point-count={data.length}
      data-points={JSON.stringify(data.map((p) => [isoDay(new Date(p.date)), p.value]))}
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 25 }}>
            <XAxis
              dataKey="date"
              type="number"
              domain={['dataMin', 'dataMax']}
              scale="time"
              ticks={xTicks}
              interval={0}
              tickFormatter={fmtX}
              angle={-45}
              textAnchor="end"
              height={50}
              tick={{ fontSize: 15 }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 15 }}
              width={44}
              domain={[0, 100]}
              tickFormatter={(v: number) => `${v}%`}
              tickLine={false}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload || payload.length === 0) return null
                const p = payload[0].payload as CoverageTrendPoint
                return (
                  <div className="rounded border border-gray-200 bg-white px-2 py-1 text-sm shadow-md">
                    <div className="text-gray-600">Through {fmtFull(p.date)}</div>
                    <div className="text-gray-900">
                      {p.value}% <span className="text-gray-400">({p.active} of {p.total} {unit})</span>
                    </div>
                  </div>
                )
              }}
            />
            <ReferenceLine y={100} stroke="#e5e7eb" strokeWidth={1} />
            {yearLines.map((ts) => <ReferenceLine key={ts} {...yearBoundaryLineProps(ts)} />)}
            <Area
              type="monotone"
              dataKey="value"
              stroke={COVERAGE_COLOR}
              strokeWidth={2}
              fill={COVERAGE_COLOR}
              fillOpacity={0.2}
              dot={data.length <= 24}
              activeDot={{ r: 8 }}
              name="Coverage"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default CoverageTrendView
